import type {
  PolicyInsight,
  PolicyInsightType,
  PolicyMeta,
  PredicateData,
} from './types'
import { INSIGHT_TYPES } from './types'

/* ponytail: the pure half of JDM_LAYER_SYNC_PLAN.md step 2 — tree <-> Nucleus
   JDM mapping. No network; imports only types from ./types. */

export interface NucleusTreeNode {
  node_label: string
  predicate: string
  yes_target: string
  no_target: string
}

export interface NucleusLeaf {
  label: string
  insightType: string
  insightText: string
  editType: string
  carc: string
  rarc: string
}

export interface NucleusDatasetBindingIn {
  key: string
  source: string
  values: string[]
  /** The decision nodes the list is bound to (D6b). The tree route's
   *  `DatasetBindingIn` ignores unknown fields until it reads this one. */
  node_labels: string[]
}

export interface NucleusTreeEdit {
  tree: NucleusTreeNode[]
  leaves: NucleusLeaf[]
  dataset_bindings?: NucleusDatasetBindingIn[]
}

export interface NucleusJdmNode {
  id?: string
  type?: string
  name?: string
  content?: Record<string, unknown>
}

export interface NucleusJdmEdge {
  sourceId?: string
  targetId?: string
  sourceHandle?: string
}

export interface NucleusJdm {
  nodes?: NucleusJdmNode[]
  edges?: NucleusJdmEdge[]
}

export interface NucleusJdmVariant {
  variant_id: string
  jdm?: NucleusJdm
  spec?: { tree?: NucleusTreeNode[] }
}

/** The pure tree slice jdm.ts maps to/from Nucleus — the shared shape of a
 * policy's `nodes` + `insights`. */
export interface JdmTreeView {
  nodes: PredicateData[]
  insights: PolicyInsight[]
}

/** The Nucleus card variant a policy was drafted from: NURO candidates are
 * `${sourceId}:${variantId}`; hand-added candidates and adopted policies have
 * none. */
export const jdmLinkOf = (
  meta: Pick<PolicyMeta, 'sourceId' | 'candidateId'>,
): { docId: string; variantId: string } | null => {
  if (!meta.sourceId || !meta.candidateId) return null
  const prefix = `${meta.sourceId}:`
  if (!meta.candidateId.startsWith(prefix)) return null
  const variantId = meta.candidateId.slice(prefix.length)
  return variantId ? { docId: meta.sourceId, variantId } : null
}

export const treeToNucleus = (
  tree: JdmTreeView,
  codeLists?: Record<
    string,
    { name: string; codes: string[]; nodeIds: string[] }
  >,
): NucleusTreeEdit => {
  const edit: NucleusTreeEdit = {
    tree: tree.nodes.map((node) => ({
      node_label: node.label,
      predicate: node.predicate,
      yes_target: node.yesPath,
      no_target: node.noPath,
    })),
    leaves: tree.insights.map((insight) => ({
      label: insight.label,
      insightType: insight.insightType ?? '',
      insightText: insight.insightText,
      editType: insight.editType?.edit_type ?? '',
      carc: insight.reasonCodeLevel1 ?? '',
      rarc: insight.reasonCodeLevel2 ?? '',
    })),
  }
  const lists = codeLists ? Object.values(codeLists) : []
  if (lists.length) {
    edit.dataset_bindings = lists.map((list) => ({
      key: list.name,
      source: 'policy-studio',
      values: list.codes,
      node_labels: tree.nodes
        .filter((node) => list.nodeIds.includes(node.id))
        .map((node) => node.label),
    }))
  }
  return edit
}

/** JSON-decodes a ZEN condition value; non-JSON (or a non-string result)
 * falls back to the trimmed raw text. */
const decodeZen = (raw: string): string => {
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed === 'string') return parsed
  } catch {
    /* not JSON — fall through to the trimmed raw string */
  }
  return raw.trim()
}

const expressionRow = (node: NucleusJdmNode, key: string): string => {
  const rows = node.content?.expressions
  if (!Array.isArray(rows)) return ''
  const row = rows.find(
    (candidate): candidate is { key: string; value: unknown } =>
      typeof candidate === 'object' &&
      candidate !== null &&
      'key' in candidate &&
      candidate.key === key,
  )
  return typeof row?.value === 'string' ? decodeZen(row.value) : ''
}

interface SwitchStatement {
  id: string
  isDefault?: boolean
}

/** Ports Nucleus's `policy_bundle.py` `_spec_from_jdm` to TypeScript, without
 * its `tree_normalize` renumbering step. Malformed input (no `jdm`,
 * non-array nodes) returns empty arrays; it never throws. */
export const nucleusToTree = (
  variant: NucleusJdmVariant,
  current: JdmTreeView,
  newId: () => string = () => crypto.randomUUID(),
): JdmTreeView => {
  const nodes = variant.jdm?.nodes
  if (!Array.isArray(nodes)) return { nodes: [], insights: [] }
  const edges = Array.isArray(variant.jdm?.edges)
    ? (variant.jdm?.edges ?? [])
    : []

  const usedIds = new Set<string>()

  const takeNodeId = (label: string, predicate: string): string => {
    const byLabel = current.nodes.find(
      (n) => !usedIds.has(n.id) && n.label === label,
    )
    if (byLabel) {
      usedIds.add(byLabel.id)
      return byLabel.id
    }
    const byPredicate = current.nodes.find(
      (n) => !usedIds.has(n.id) && n.predicate.trim() === predicate.trim(),
    )
    if (byPredicate) {
      usedIds.add(byPredicate.id)
      return byPredicate.id
    }
    return newId()
  }

  const matchInsight = (
    label: string,
    insightType: string,
    insightText: string,
  ): PolicyInsight | undefined => {
    const byLabel = current.insights.find(
      (i) => !usedIds.has(i.id) && i.label === label,
    )
    if (byLabel) {
      usedIds.add(byLabel.id)
      return byLabel
    }
    const byContent = current.insights.find(
      (i) =>
        !usedIds.has(i.id) &&
        (i.insightType ?? '') === insightType &&
        i.insightText === insightText,
    )
    if (byContent) {
      usedIds.add(byContent.id)
      return byContent
    }
    return undefined
  }

  // First pass: map every leaf (expressionNode) id to its decoded label, so
  // switch-edge targets can resolve to an outcome label.
  const leafLabelById = new Map<string, string>()
  for (const node of nodes) {
    if (node.type !== 'expressionNode' || !node.id) continue
    const label = expressionRow(node, 'label') || node.id.replace(/^leaf_/, '')
    leafLabelById.set(node.id, label)
  }

  const resolveTarget = (targetId: string | undefined): string => {
    if (!targetId) return ''
    if (targetId.startsWith('sw_')) return targetId.replace(/^sw_/, '')
    return leafLabelById.get(targetId) ?? ''
  }

  const outNodes: PredicateData[] = []
  const outInsights: PolicyInsight[] = []

  for (const node of nodes) {
    if (!node.id) continue
    if (node.type === 'switchNode') {
      const label = node.id.replace(/^sw_/, '')
      const specRow = variant.spec?.tree?.find((t) => t.node_label === label)
      const sep = node.name?.indexOf(' · ') ?? -1
      const predicate =
        specRow?.predicate ?? (sep >= 0 ? (node.name ?? '').slice(sep + 3) : '')
      const statements = node.content?.statements
      const list = Array.isArray(statements)
        ? (statements as SwitchStatement[])
        : []
      const yesStatementId = list[0]?.id
      const noStatementId = list.find((s) => s.isDefault)?.id
      const yesEdge = edges.find(
        (e) => e.sourceId === node.id && e.sourceHandle === yesStatementId,
      )
      const noEdge = edges.find(
        (e) => e.sourceId === node.id && e.sourceHandle === noStatementId,
      )
      const id = takeNodeId(label, predicate)
      /* Settings live only in Studio; Nucleus never sees them, so a pull keeps ours. */
      const settings = current.nodes.find((n) => n.id === id)?.settings
      outNodes.push({
        id,
        label,
        predicate,
        yesPath: resolveTarget(yesEdge?.targetId),
        noPath: resolveTarget(noEdge?.targetId),
        ...(settings ? { settings } : {}),
      })
    } else if (node.type === 'expressionNode') {
      const label = leafLabelById.get(node.id) ?? node.id.replace(/^leaf_/, '')
      const insightTypeRaw = expressionRow(node, 'insightType')
      const insightText = expressionRow(node, 'insightText')
      const editTypeRaw = expressionRow(node, 'editType')
      const carc = expressionRow(node, 'carc')
      const rarc = expressionRow(node, 'rarc')
      const matched = matchInsight(label, insightTypeRaw, insightText)
      const id = matched?.id ?? newId()
      const insightType = (INSIGHT_TYPES as readonly string[]).includes(
        insightTypeRaw,
      )
        ? (insightTypeRaw as PolicyInsightType)
        : undefined
      const editType =
        matched?.editType?.edit_type === editTypeRaw
          ? matched.editType
          : editTypeRaw
            ? /* ponytail: Nucleus carries the edit type's name, not PaAPI's
                 id; the id resolves when someone re-picks it in the editor. */
              { id: '', edit_type: editTypeRaw }
            : undefined
      outInsights.push({
        id,
        label,
        insightText,
        ...(insightType ? { insightType } : {}),
        ...(editType ? { editType } : {}),
        ...(carc ? { reasonCodeLevel1: carc } : {}),
        ...(rarc ? { reasonCodeLevel2: rarc } : {}),
        ...(matched?.paymentInsight !== undefined
          ? { paymentInsight: matched.paymentInsight }
          : {}),
        ...(matched?.paymentCalculationDefinition !== undefined
          ? {
              paymentCalculationDefinition:
                matched.paymentCalculationDefinition,
            }
          : {}),
        ...(matched?.insightEligibleForChange !== undefined
          ? { insightEligibleForChange: matched.insightEligibleForChange }
          : {}),
        ...(matched?.changeFields !== undefined
          ? { changeFields: matched.changeFields }
          : {}),
        ...(matched?.changeFieldsNewValueCalculationDefinition !== undefined
          ? {
              changeFieldsNewValueCalculationDefinition:
                matched.changeFieldsNewValueCalculationDefinition,
            }
          : {}),
        ...(matched?.settings ? { settings: matched.settings } : {}),
      })
    }
  }

  return { nodes: outNodes, insights: outInsights }
}
